import { useEffect, useState } from 'react';
import api from '../api/client';

/**
 * The six programmes are managed in the database so the team can edit copy,
 * seats and details from the console without a redeploy. These bundled copies
 * are the fallback: if the API is slow or down, the page still renders real
 * content instead of an empty grid or a spinner.
 */
export const FALLBACK_TRACKS = [
  {
    slug: 'exam-operations',
    title: 'Exam Operations & Logistics',
    tagline: 'Learn how a 40,000-candidate exam day actually runs',
    description:
      'Work alongside the team that plans centre allocation, invigilator rosters, biometric attendance and question-paper custody for government recruitment exams. You will sit in on a real centre readiness audit and own a slice of the day-of control room.',
    duration: '8 weeks',
    mode: 'Onsite',
    stipend: 'Decided after selection',
    seats: 8,
    skills: ['Operations planning', 'MS Excel', 'Vendor coordination',
      'Incident reporting', 'Field audit', 'Scheduling',
      'Stakeholder communication', 'Risk logging'],
    image_url: '/assets/images/Exam_Snapshots/Picture3.png',
    icon: 'fa-clipboard',
  },
  {
    slug: 'software-development',
    title: 'Software Development',
    tagline: 'Build the systems the business actually runs on',
    description:
      'Work on the server-side software behind our exam management, candidate verification and client reporting platforms. You will take a feature from a written requirement through to a tested, reviewed, deployed change — writing APIs, modelling data, and handling the awkward cases that only show up with real users.',
    duration: '12 weeks',
    mode: 'Hybrid',
    stipend: 'Decided after selection',
    seats: 10,
    skills: ['Java', 'JavaScript', 'Node.js', 'Object-oriented design', 'REST APIs',
      'SQL', 'Git', 'Debugging', 'Unit testing', 'Code review',
      'Data structures'],
    image_url: '/assets/images/IT_Consulting_Images/modernization.jpg',
    icon: 'fa-terminal',
  },
  {
    slug: 'full-stack-development',
    title: 'Full-Stack Web Development',
    tagline: 'Ship features on the platforms our clients use every day',
    description:
      'Build with HTML, CSS, JavaScript, React, Node.js and PostgreSQL on our internal exam-management and client portals. You get a real ticket queue, code review from a senior engineer, and your work goes to production before you leave.',
    duration: '12 weeks',
    mode: 'Hybrid',
    stipend: 'Decided after selection',
    seats: 10,
    skills: ['HTML', 'CSS', 'JavaScript', 'React', 'Node.js', 'Express',
      'PostgreSQL', 'REST APIs', 'Responsive design', 'Git', 'Testing'],
    image_url: '/assets/images/IT_Consulting_Images/about-image.jpg',
    icon: 'fa-code',
  },
  {
    slug: 'ai-machine-learning',
    title: 'AI & Machine Learning',
    tagline: 'Train models on data that comes from real operations',
    description:
      'Work with our operations and candidate datasets to build models that answer questions the business actually asks — forecasting centre load, flagging anomalies in attendance data, and classifying incoming enquiries. You will handle the unglamorous parts too: cleaning data, choosing honest metrics, and explaining why a model should not be trusted yet.',
    duration: '12 weeks',
    mode: 'Hybrid',
    stipend: 'Decided after selection',
    seats: 6,
    skills: ['Python', 'Machine Learning', 'Deep Learning', 'Neural networks',
      'NumPy', 'Pandas', 'scikit-learn', 'TensorFlow', 'Model evaluation',
      'Data preprocessing', 'Prompt engineering'],
    image_url: '/assets/images/IT_Consulting_Images/innovation-focus.jpg',
    icon: 'fa-brain',
  },
  {
    slug: 'devops-cloud',
    title: 'DevOps & Cloud Infrastructure',
    tagline: 'Keep the systems up on the days they cannot go down',
    description:
      'Learn how our services are built, deployed and monitored. You will work on Linux servers, write deployment pipelines, containerise an application, and take part in a real incident review. Exam days have no maintenance window, which makes this a good place to learn why reliability work matters.',
    duration: '12 weeks',
    mode: 'Hybrid',
    stipend: 'Decided after selection',
    seats: 6,
    skills: ['Linux', 'Shell scripting', 'Git', 'CI/CD', 'Docker', 'Nginx',
      'Cloud basics', 'Monitoring & logging', 'Deployment automation',
      'Networking'],
    image_url: '/assets/images/IT_Consulting_Images/it-potential.jpeg',
    icon: 'fa-server',
  },
  {
    slug: 'qa-test-automation',
    title: 'Testing & Test Automation',
    tagline: 'Find the failure before a candidate does',
    description:
      'Own the quality of features going into our platforms. You will write test plans, run exploratory testing, and build an automated regression suite that runs on every deployment. On exam-critical systems a missed bug has consequences, so you learn to think about failure properly rather than tick boxes.',
    duration: '10 weeks',
    mode: 'Hybrid',
    stipend: 'Decided after selection',
    seats: 6,
    skills: ['Manual testing', 'Test case design', 'Selenium', 'Automation testing',
      'API testing', 'Postman', 'JavaScript', 'Bug reporting',
      'Regression testing', 'CI integration'],
    image_url: '/assets/images/IT_Consulting_Images/continous-improvement.jpg',
    icon: 'fa-vial',
  },
  {
    slug: 'data-analytics',
    title: 'Data & Reporting Analytics',
    tagline: 'Turn exam and campaign data into decisions',
    description:
      'Work with candidate throughput, centre utilisation and campaign performance data. You will write SQL against live datasets, build dashboards the operations team checks each morning, and present one findings deck to the leadership.',
    duration: '10 weeks',
    mode: 'Hybrid',
    stipend: 'Decided after selection',
    seats: 6,
    skills: ['SQL', 'Python', 'Excel', 'Power BI', 'Statistics', 'Data cleaning',
      'Data visualisation', 'Reporting', 'Storytelling'],
    image_url: '/assets/images/IT_Consulting_Images/data-driven-decision.png',
    icon: 'fa-chart',
  },
  {
    slug: 'cyber-security',
    title: 'Cyber Security & Infrastructure',
    tagline: 'Protect systems that cannot afford to fail on exam day',
    description:
      'Shadow our infrastructure team on network hardening, CCTV and jammer deployment, access control and incident response for high-stakes examination centres. Includes a supervised vulnerability assessment of a staging environment and a written findings report.',
    duration: '10 weeks',
    mode: 'Onsite',
    stipend: 'Decided after selection',
    seats: 5,
    skills: ['Networking', 'Linux', 'Cyber security fundamentals', 'Access control',
      'Firewalls', 'Vulnerability assessment', 'Penetration testing basics',
      'Risk assessment', 'Incident response', 'Security auditing',
      'Cryptography basics'],
    image_url: '/assets/images/SECURITY_MANAGEMENT/Picture2.png',
    icon: 'fa-shield',
  },
  {
    slug: 'digital-marketing',
    title: 'Digital Marketing & Growth',
    tagline: 'Run live campaigns with a real budget behind them',
    description:
      'Plan and run search, social and email campaigns for Starparth and selected clients. You will own keyword research, ad copy, landing-page tests and the weekly performance report — with a live budget, not a simulation.',
    duration: '8 weeks',
    mode: 'Hybrid',
    stipend: 'Decided after selection',
    seats: 8,
    skills: ['SEO', 'Google Ads', 'Meta Ads', 'Content writing', 'Google Analytics',
      'Email marketing', 'Landing page testing', 'Social media'],
    image_url: '/assets/images/about/about-4.jpg',
    icon: 'fa-megaphone',
  },
  {
    slug: 'hr-talent',
    title: 'HR & Manpower Management',
    tagline: 'Staff an exam day — 2,000 people, one deadline',
    description:
      'Learn large-scale recruitment, verification and deployment of invigilators and field staff. You will run screening calls, manage the deployment tracker and help close a real staffing cycle end to end.',
    duration: '8 weeks',
    mode: 'Onsite',
    stipend: 'Decided after selection',
    seats: 6,
    skills: ['Recruitment', 'Screening', 'Interviewing', 'Documentation',
      'Scheduling', 'MS Excel', 'Communication', 'Onboarding'],
    image_url: '/assets/images/MANPOWER_MANAGEMENT/Picture1.png',
    icon: 'fa-users',
  },
];

/** Every skill offered across all tracks, for the application form's chips. */
export const ALL_SKILLS = [...new Set(FALLBACK_TRACKS.flatMap((t) => t.skills))].sort();

export function useTracks() {
  const [tracks, setTracks] = useState(FALLBACK_TRACKS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api.get('/api/internships/tracks')
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data?.data) && data.data.length) {
          setTracks(data.data);
        }
      })
      .catch(() => { /* keep the bundled copy */ })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };
  }, []);

  return { tracks, isLoading };
}
